import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, Clock, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const offices = [
  {
    name: 'Europe Regional Office - Sweden',
    address: 'Stockholm, Sweden',
    email: 'contact@opad.eu',
    type: 'Regional Office'
  },
  {
    name: 'Finland Office',
    address: 'Helsinki, Finland',
    email: 'finland@opad.eu',
    type: 'Regional'
  },
  {
    name: 'United States Office',
    address: 'New York, NY, USA',
    email: 'usa@opad.eu',
    type: 'Country Office'
  },
  {
    name: 'West Africa Office - Sierra Leone',
    address: 'Freetown, Sierra Leone',
    email: 'westafrica@opad.eu',
    type: 'Regional Office'
  },
  {
    name: 'East Africa Office - Ethiopia',
    address: 'Addis Ababa, Ethiopia',
    email: 'eastafrica@opad.eu',
    type: 'Regional Office'
  },
  {
    name: 'Southern Africa Office - Zambia',
    address: 'Lusaka, Zambia',
    email: 'southernafrica@opad.eu',
    type: 'Regional Office'
  }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will respond within 2-3 business days.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section with Abstract Background */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-white/20 rounded-tr-[150px]" />
          <div className="absolute top-[40%] left-[10%] w-[120px] h-[120px] bg-[#E08B7A]/20 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Contact Us
              </h1>
              <p className="text-lg text-slate-800">
                Have questions or want to learn more about our work? Get in touch with us.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Info & Form Section with Abstract Background */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#7BC4E2]/15 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#E08B7A]/10 rounded-tl-[180px]" />
          <div className="absolute top-[50%] right-[5%] w-[150px] h-[150px] bg-[#E5B75F]/15 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6 bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100" data-testid="contact-form">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Select 
                    value={formData.subject}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="contact-subject">
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="volunteer">Volunteer Information</SelectItem>
                      <SelectItem value="donation">Donation Query</SelectItem>
                      <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                      <SelectItem value="media">Media Request</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="How can we help you?"
                    className="mt-1"
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#4A7C59] hover:bg-[#3A6B49] text-white rounded-full py-6"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>Sending...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                <span>We typically respond within 2-3 business days</span>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Get in Touch
              </h2>

              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4 bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100">
                  <div className="w-12 h-12 bg-[#7BC4E2]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#7BC4E2]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">General Contact</h3>
                    <a href="mailto:contact@opad.eu" className="text-slate-600 hover:text-[#7BC4E2] transition-colors">
                      contact@opad.eu
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100">
                  <div className="w-12 h-12 bg-[#E08B7A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#E08B7A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Enquiries</h3>
                    <a href="mailto:enquiries@opad.eu" className="text-slate-600 hover:text-[#7BC4E2] transition-colors">
                      enquiries@opad.eu
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100">
                  <div className="w-12 h-12 bg-[#4A7C59]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-[#4A7C59]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Website</h3>
                    <a href="https://www.opad.eu" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-[#7BC4E2] transition-colors">
                      www.opad.eu
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100">
                <h3 className="font-bold text-slate-900 mb-4">Business Hours</h3>
                <div className="space-y-2 text-slate-600">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM (CET)</p>
                  <p>Saturday - Sunday: Closed</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Offices Directory with Abstract Background */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#E08B7A]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#4A7C59]/15 rounded-tr-[150px]" />
          <div className="absolute top-[30%] left-[50%] w-[180px] h-[180px] bg-[#7BC4E2]/15 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Global Offices Directory
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Reach out to our regional offices for location-specific inquiries.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offices.map((office, index) => (
              <motion.div
                key={index}
                className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`office-${index}`}
              >
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  office.type === 'Headquarters' 
                    ? 'bg-[#E08B7A]/10 text-[#E08B7A]' 
                    : 'bg-[#7BC4E2]/10 text-[#7BC4E2]'
                }`}>
                  {office.type}
                </span>
                <h3 className="font-bold text-slate-900 mt-3 mb-2">{office.name}</h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {office.address}
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${office.email}`} className="hover:text-[#7BC4E2] transition-colors">
                      {office.email}
                    </a>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
